import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { InfoIcon, StarIcon } from 'app/components/icons'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import Assets from 'app/configs/assetConfigs'
import clsx from 'clsx'
import { usePlace } from 'commons/usePlace'
import { FormattedMessage, useIntl } from 'react-intl'
import { twMerge } from 'tailwind-merge'
import { addImageKitToBaseUrl } from '../composeMessage/tiptapEditor/TiptapUtils'
import Button from './Button'
import GenerateError from './GenerateError'

const useStyle = makeStyles(() => ({
  recentImagesCheck: {
    '& .MuiIconButton-label': {
      backgroundColor: '#545353',
      color: '#ffffff !important',
      borderRadius: '.5rem !important',
    },
    '& .MuiCheckbox-colorPrimary.Mui-checked': {
      opacity: 'unset !important',
    },
    '& .MuiCheckbox-colorPrimary.Mui-checked:hover': {
      backgroundColor: 'unset !important',
    },
  },
}))

const imageStylesOptions = (formatMessage) => [
  { label: formatMessage({ id: 'abby.style.realistic' }), value: 'realistic' },
  {
    label: formatMessage({ id: 'abby.style.minimalist' }),
    value: 'minimalist',
  },
  {
    label: formatMessage({ id: 'abby.style.corporate_illustration' }),
    value: 'corporate illustration',
  },
  {
    label: formatMessage({ id: 'abby.style.infographic' }),
    value: 'infographic',
  },
  { label: formatMessage({ id: 'abby.style.abstract' }), value: 'abstract' },
  { label: formatMessage({ id: 'abby.style.elegant' }), value: 'elegant' },
]

const imageOrientationOptions = (formatMessage) => [
  {
    label: formatMessage({ id: 'abby.orientation.square' }),
    value: 'square',
    previewClassName: 'w-24 h-24',
  },
  {
    label: formatMessage({ id: 'abby.orientation.portrait' }),
    value: 'portrait',
    previewClassName: 'w-24 h-36',
  },
  {
    label: formatMessage({ id: 'abby.orientation.landscape' }),
    value: 'landscape',
    previewClassName: 'w-36 h-24',
  },
]

const StyledTabs = withStyles((theme) => {
  return {
    root: {
      borderBottom: '1px solid ' + theme.colors['grey-100'],
    },
    indicator: {
      backgroundColor: theme.colors.secondary,
    },
  }
})(Tabs)

const StyledTab = withStyles((theme) => ({
  root: {
    textTransform: 'none',
    color: theme.colors.secondary,
  },
}))(Tab)

export default function GenerateImageWithAIDialog({
  isOpen,
  promptText,
  isGenerating,
  isKeywordsGenerating,
  image,
  recentImages,
  selectedRecentImages,
  imageStyle,
  imageOrientation,
  isSummarizeEnabled,
  onCloseClick,
  onGenerateImageWithPromptClick,
  onPromptTextChange,
  onCancelGenerateImageClick,
  onUseImageClick,
  onImageStyleChange,
  onImageOrientationChange,
  onSummarizeClick,
  onRecentImageSelectChange,
  credits,
  tabIndex,
  onTabChange,
  isUseButtonDisabled,
  error,
  onCancelGenerateImageConfirmClick,
}) {
  const place = usePlace()
  const isChanging = place.aiBillingCharged
  const classes = useStyle()
  const { formatMessage } = useIntl()
  if (!isOpen) return null

  const formattedImageStylesOptions = imageStylesOptions(formatMessage)
  const formattedImageOrientationOptions =
    imageOrientationOptions(formatMessage)

  function renderContent() {
    return (
      <div className="md:h-full">
        <div className="h-full flex flex-col-reverse md:flex-row md:space-x-20 lg:space-x-44 space-y-12 md:space-y-0">
          <div
            className={clsx(
              'w-full flex-1 mt-20',
              tabIndex === 1 && 'hidden md:block',
            )}
          >
            <div className="h-full flex flex-col space-y-12">
              <div className="space-y-6">
                <h2 className="text-primary-400 text-14 font-medium">
                  <FormattedMessage id="ai.dialog.images.imageStyles.label" />
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 auto-rows-fr">
                  {formattedImageStylesOptions.map((option) => (
                    <label
                      className="block cursor-pointer"
                      id={option}
                      key={option.value}
                    >
                      <input
                        type="checkbox"
                        name="imageStyle"
                        className="hidden peer"
                        value={option.value}
                        checked={option.value === imageStyle}
                        onChange={(e) => {
                          onImageStyleChange(
                            e.target.value === imageStyle
                              ? undefined
                              : e.target.value,
                          )
                        }}
                      />
                      <div className="bg-white text-primary-300 border border-gray-300 flex flex-col space-x-6 items-center justify-center h-full p-24 peer-checked:border-secondary peer-checked:text-secondary rounded-lg peer-checked:shadow text-center transition">
                        <h3 className="text-14 font-medium peer-checked:font-semibold">
                          {option.label}
                        </h3>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-primary-400 text-14 font-medium">
                  <FormattedMessage id="ai.dialog.images.imageOrientation.label" />
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {formattedImageOrientationOptions.map((option) => (
                    <label
                      className="block cursor-pointer"
                      id={option.label}
                      key={option.value}
                    >
                      <input
                        type="checkbox"
                        name="imageOrientation"
                        className="hidden peer"
                        value={option.value}
                        checked={option.value === imageOrientation}
                        onChange={(e) => {
                          onImageOrientationChange(
                            e.target.value === imageOrientation
                              ? undefined
                              : e.target.value,
                          )
                        }}
                      />
                      <div className="bg-white text-primary-300 flex flex-col space-x-6 items-center justify-center border border-gray-300 h-full p-16 peer-checked:border-secondary peer-checked:text-secondary rounded-lg peer-checked:shadow text-center transition">
                        <div>
                          <div
                            className={clsx(
                              'm-auto bg-primary-350',
                              option.previewClassName,
                            )}
                          ></div>
                        </div>
                        <h3 className="text-14 font-medium peer-checked:font-semibold">
                          {option.label}
                        </h3>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-6 h-full">
                <div className="flex justify-between w-full">
                  <div className="flex">
                    <h2 className="text-primary-400 text-14 font-medium">
                      <FormattedMessage id="ai.dialog.images.prompt.label" />
                    </h2>
                    <PrimaryTooltip
                      title={formatMessage({
                        id: 'ai.image.prompt.tooltip.label',
                      })}
                    >
                      <div className="mx-4">
                        <InfoIcon color="#183560" classes="w-16" />
                      </div>
                    </PrimaryTooltip>
                  </div>

                  {isSummarizeEnabled && (
                    <PrimaryTooltip
                      title={formatMessage({
                        id: 'ai.imageSummarize.tooltip',
                      })}
                    >
                      <button
                        className={clsx(
                          `px-6 py-1 font-medium text-12 block text-secondary border-0 rounded-lg transition`,
                          isKeywordsGenerating ? 'opacity-50' : 'opacity-100',
                        )}
                        onClick={onSummarizeClick}
                        disabled={isKeywordsGenerating}
                      >
                        {formatMessage({
                          id: isKeywordsGenerating
                            ? 'ai.dialog.summarizing'
                            : 'ai.dialog.summarize',
                        })}
                      </button>
                    </PrimaryTooltip>
                  )}
                </div>

                <textarea
                  value={promptText}
                  onChange={(e) => onPromptTextChange(e.target.value)}
                  rows="8"
                  className="block p-16 w-full text-base text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-secondary focus:border-secondary"
                ></textarea>
              </div>

              {isGenerating ? (
                <Button
                  onClick={() => {
                    onCancelGenerateImageClick()
                  }}
                  className="flex justify-center"
                >
                  <div className="flex items-center space-x-16">
                    <Typography variant="subtitle2">
                      <FormattedMessage id="ai.dialog.generating.cancel" />
                    </Typography>
                  </div>
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    onTabChange(0)
                    onGenerateImageWithPromptClick()
                  }}
                  className={clsx(
                    'flex justify-center text-secondary hover:bg-gray-200 border transition rounded-8 h-48',
                    promptText
                      ? 'bg-white opacity-100 border-secondary'
                      : 'bg-gray-200 opacity-50 border-transparent',
                  )}
                  disabled={!promptText}
                >
                  <div className="flex items-center space-x-16">
                    <StarIcon className="w-24 h-24" />
                    <Typography variant="subtitle2">
                      <FormattedMessage id="ai.dialog.generate" />
                    </Typography>
                  </div>
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:px-60 space-y-16 mb-8">
            <StyledTabs
              value={tabIndex}
              onChange={(_, value) => {
                onTabChange(value)
              }}
            >
              <StyledTab
                label={formatMessage({
                  id: isGenerating
                    ? 'ai.dialog.generating'
                    : image
                      ? 'ai.dialog.images.generated'
                      : 'ai.dialog.images.empty',
                })}
              />
              {recentImages && recentImages.length > 0 && !isGenerating && (
                <StyledTab
                  label={formatMessage({
                    id: 'ai.dialog.images.recentHistory',
                  })}
                />
              )}
            </StyledTabs>
            <div className="sm:w-512 md:w-560 lg:w-580 mx-auto space-y-12 flex flex-col h-full justify-between transition overflow-y-hidden">
              {tabIndex === 0 ? (
                <>
                  {image && !isGenerating ? (
                    <div className="group rounded border border-gray-200 bg-gray-100 relative overflow-hidden aspect-square">
                      <img
                        className="w-full h-full object-contain aspect-square"
                        src={addImageKitToBaseUrl(image.src)}
                        alt="AI generated"
                      />
                    </div>
                  ) : (
                    <div
                      className={clsx(
                        'group rounded aspect-square border border-gray-200 bg-gray-100 relative overflow-hidden flex flex-col items-center justify-center space-y-4',
                        isGenerating &&
                          'animate-pulse bg-gradient-to-br from-gray-100 via-gray-400 to-gray-300',
                      )}
                    >
                      {isGenerating ? (
                        <>
                          <div className="py-20 px-16 text-center">
                            <img
                              src={Assets.AILoading}
                              width={120}
                              height={120}
                              alt="Loading"
                            />
                          </div>
                          <div className="text-gray-500 text-18 text-center max-w-256">
                            <FormattedMessage id="ai.dialog.images.generating.placeholder" />
                          </div>
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 62 47"
                            className="h-60 w-60"
                          >
                            <path
                              fill="#D4D4D4"
                              d="M53.835 4.211a2.402 2.402 0 001.492-1.543l.496-1.575a.848.848 0 011.618 0l.496 1.575c.227.72.78 1.292 1.492 1.543l1.38.486a.93.93 0 010 1.754l-1.38.486a2.402 2.402 0 00-1.492 1.543l-.496 1.575a.848.848 0 01-1.618 0l-.496-1.575a2.402 2.402 0 00-1.492-1.543l-1.38-.486a.93.93 0 010-1.754l1.38-.486zM42.36 12.88a2.401 2.401 0 001.493-1.543l1.48-4.695a.848.848 0 011.617 0l1.48 4.695c.227.72.78 1.292 1.492 1.543l4.33 1.527a.93.93 0 010 1.753l-4.33 1.527a2.402 2.402 0 00-1.492 1.543l-1.48 4.696a.848.848 0 01-1.618 0l-1.48-4.696a2.402 2.402 0 00-1.491-1.543l-4.33-1.527a.93.93 0 010-1.753l4.33-1.527z"
                            ></path>
                            <path
                              fill="#D4D4D4"
                              fillRule="evenodd"
                              d="M6.185 4.103a6.005 6.005 0 00-6.004 6.005V38.93c0 3.926 3.052 7.205 6.932 7.205H46.09c3.88 0 6.933-3.28 6.933-7.206V18.916l-1.2.4a2.402 2.402 0 00-1.55 1.618l-1.526 5.34a2.549 2.549 0 01-4.793.304l-2.594-6.053a2.402 2.402 0 00-1.134-1.202l-5.721-2.861c-.462-.23-.945-.509-1.13-.99a1.462 1.462 0 01-.078-.767c.118-.737.879-1.116 1.586-1.352l3.851-1.284a4.804 4.804 0 003.039-3.038l1.642-4.927H6.185zm44.03 36.823l-17.111-17.11a2.402 2.402 0 00-3.397 0l-6.21 6.21a2.402 2.402 0 01-3.398 0l-1.407-1.407a2.402 2.402 0 00-3.396 0L2.989 40.926c.722 1.675 2.323 2.808 4.124 2.808H46.09c1.8 0 3.402-1.133 4.124-2.808z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                          <div className="text-gray-500 text-18 text-center max-w-256">
                            <FormattedMessage id="ai.dialog.images.placeholder" />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 auto-rows-min gap-5 overflow-y-scroll">
                    {recentImages.map((image) => {
                      return (
                        <div
                          key={image.url}
                          className="relative aspect-square rounded bg-gray-100 overflow-hidden"
                        >
                          <img
                            className="object-contain aspect-square w-full h-full"
                            src={addImageKitToBaseUrl(image.url)}
                            alt="Recent Image"
                          />
                          <Checkbox
                            className={clsx(
                              'absolute right-2 top-2',
                              classes.recentImagesCheck,
                            )}
                            color="primary"
                            checked={selectedRecentImages.has(image.url)}
                            onChange={(e) => {
                              onRecentImageSelectChange(
                                e.target.checked,
                                image.url,
                              )
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>
                </>
              )}

              <Button
                onClick={() => {
                  onUseImageClick()
                }}
                className={twMerge(
                  'flex justify-center border transition rounded-8 h-48',
                  isUseButtonDisabled
                    ? 'bg-gray-200 opacity-50 hover:bg-gray-200 border-transparent'
                    : 'bg-secondary opacity-100 text-white hover:bg-secondary-200 border-secondary',
                )}
                disabled={isUseButtonDisabled}
              >
                <div className="flex items-center space-x-16">
                  <StarIcon className="w-24 h-24" />
                  <Typography variant="subtitle2">
                    <FormattedMessage id="ai.dialog.images.insertImage.button" />
                  </Typography>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Dialog open={isOpen} fullScreen maxWidth="md">
      <DialogTitle className="py-4">
        <div className="flex items-center space-x-8 justify-between py-5">
          <div className="flex flex-col">
            <Typography
              variant="subtitle1"
              className="w-full text-primary-400 font-bold"
            >
              <FormattedMessage id="ai.dialog.images.title" />
            </Typography>
            <div className="flex items-center space-x-8">
              <InfoIcon color="#183560" classes="w-20" />
              <Typography
                variant="subtitle2"
                className="w-full text-primary-300 font-normal"
              >
                <FormattedMessage
                  id={
                    isChanging
                      ? 'ai.dialog.subtitle.prompting.changing'
                      : 'ai.dialog.subtitle.prompting.no.changing'
                  }
                />
              </Typography>
            </div>
          </div>

          <div className="flex items-center">
            <span
              className={`pl-16 pr-20 py-8 rounded-full flex items-center space-x-6 bg-orange-500 text-base text-white`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-24 w-24"
                viewBox="0 0 24 25"
              >
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10.773 20.864C6.48 20.864 3 17.2 3 12.682S6.48 4.5 10.773 4.5c4.292 0 7.772 3.663 7.772 8.182s-3.48 8.182-7.772 8.182z"
                ></path>
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12.818 10.064a2.932 2.932 0 00-1.84-.655c-1.695 0-3.069 1.465-3.069 3.273s1.374 3.272 3.068 3.272c.69 0 1.328-.243 1.841-.654"
                ></path>
                <path
                  stroke="#fff"
                  strokeWidth="2"
                  d="M10.364 4.5C13.909 4.5 21 5.318 21 12.682c0 7.364-7.091 8.182-10.636 8.182"
                ></path>
              </svg>

              <span>{credits ? credits.toLocaleString() : '-'}</span>
            </span>
            <Divider
              className="min-h-40 ml-16"
              variant="inset"
              orientation="vertical"
            />
            <IconButton
              aria-label="close"
              onClick={onCloseClick}
              className="ml-6"
            >
              <Icon className="text-20">close</Icon>
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent className="pb-20" dividers>
        {error ? (
          <div className="space-y-8 text-center">
            <GenerateError onRetry={onCancelGenerateImageConfirmClick} />
          </div>
        ) : (
          renderContent()
        )}
      </DialogContent>
    </Dialog>
  )
}
